import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Input from "../../component/items/Input";
import Button from "../../component/items/Button";
import Header from "../../component/items/Header";
import { dataPost } from "../../network";
import { Dialog } from "../../component/items/Dialog";
import { dialogDataTypes } from "../../component/items/itemDataType";
import { resetPasswordUrl, forgotPasswordUrl } from "../../network/const";

function ResetPassword() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const email = state?.email;
  const token = state?.token;
  const expireInSeconds = state?.expireInSeconds;

  const [form, setForm] = useState({
    otpCode: "",
    password: "",
    confirmPassword: "",
  });

  const [dialog, setDialog] = useState(false);
  const [dialogData, setDialogData] = useState({
    title: "",
    description: "",
    btnText: "",
  });

  const [timer, setTimer] = useState<number>(expireInSeconds || 0);

  useEffect(() => {
    if (!timer) return;
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          resendOtp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const dialogInit = ({ title, btnText, description }: dialogDataTypes) => {
    setDialogData({ title, btnText, description });
    setDialog(true);
  };

  const submit = async (e: any) => {
    e.preventDefault();

    if (!form.otpCode || !form.password || !form.confirmPassword) {
      dialogInit({
        title: "Validation Error",
        description: "All fields are required",
        btnText: "OK",
      });
      return;
    }

    const payload = {
      verificationCode: token,
      otpCode: parseInt(form.otpCode),
      password: form.password,
      confirmPassword: form.confirmPassword,
    };

    const res = await dataPost(resetPasswordUrl, payload, { silent: false });

    if (res.status) {
      dialogInit({
        title: "Success",
        description: "Password reset successfully. Redirecting to login...",
        btnText: "OK",
      });
      setTimeout(() => navigate("/login"), 2000);
    } else {
      dialogInit({
        title: "Error",
        description: res.message,
        btnText: "Try Again",
      });
    }
  };

  const resendOtp = async () => {
    if (!email) return;

    const res = await dataPost(forgotPasswordUrl, { email }, { silent: false });
    if (res.status && res.data.token) {
      dialogInit({
        title: "OTP Sent",
        description: "A new OTP has been sent to your email.",
        btnText: "OK",
      });

      state.token = res.data.token;
      setTimer(res.data.expireInSeconds || 86400);
    } else {
      dialogInit({
        title: "Error",
        description: res.message || "Failed to resend OTP",
        btnText: "Retry",
      });
    }
  };
  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  return (
    <div className="flex justify-center items-center h-screen">
      <div className="max-w-md w-full ">
        <Header
          heading="Reset Password"
          paragraph="Back to "
          linkName="Login"
          linkUrl="/login"
          className="mb-2"
        />

        <div className="text-center text-sm text-gray-600 mt-1">
          <span className="mr-1">Back to</span>
          <Link
            to="/forgot-password"
            className="font-medium text-purple-600 hover:text-purple-500"
          >
            Forgot Password
          </Link>
        </div>
        {/* Success Message */}
        <div className="text-center text-red-600 text-sm font-semibold mt-4">
          Password reset instructions have been sent to your email.
        </div>

        <form className="mt-4 space-y-6">
          {/* Timer Display */}
          <div className="text-center text-sm font-medium mt-2">
            {timer > 0 ? (
              <span className="text-gray-600">
                OTP expires in{" "}
                <span className="text-orange-500">{formatTime(timer)}</span>
              </span>
            ) : (
              <span className="text-red-600">
                OTP expired.{" "}
                <button
                  type="button"
                  onClick={resendOtp}
                  className="font-medium text-purple-600 hover:text-purple-500"
                >
                  Resend OTP
                </button>
              </span>
            )}
          </div>

          <Input
            id="otpCode"
            labelText="OTP Code"
            type="number"
            placeholder="Enter the OTP"
            isRequired={true}
            value={form.otpCode}
            handleChange={handleChange}
            key="otp"
            labelFor="otp-code"
            name="otp"
            customClass={""}
          />

          <Input
            id="password"
            labelText="New Password"
            type="password"
            placeholder="New Password"
            isRequired={true}
            value={form.password}
            handleChange={handleChange}
            key="password"
            labelFor="new-password"
            name="password"
            customClass={""}
          />
          <Input
            id="confirmPassword"
            labelText="Confirm Password"
            type="password"
            placeholder="Confirm Password"
            isRequired={true}
            value={form.confirmPassword}
            handleChange={handleChange}
            key="confirm-password"
            labelFor="confirm-password"
            name="confirm-password"
            customClass={""}
          />
          <Button text="Reset Password" action="button" handleSubmit={submit} />
        </form>

        <Dialog
          open={dialog}
          handleOpen={() => setDialog(!dialog)}
          title={dialogData.title}
          description={dialogData.description}
          buttonText={dialogData.btnText}
        />
      </div>
    </div>
  );
}

export default ResetPassword;
